/**
 * `help/guides.ts` — the in-app guides behind the top bar's Help button.
 *
 * One guide per part of the lead's app. Each opens with the same three
 * questions (why you come here, what you see first, what you do straight
 * away), then a numbered "How to", then "Good to know" where it helps.
 * The copy is read from `docs/USER_FLOW.md` (the live flow) — when a page
 * changes, its guide changes in the same PR. "You" is the roster lead.
 *
 * Paragraphs are plain text; `**bold**` is drawn as <strong> by the drawer.
 * A separate documentation page will come later; these are the first drafts.
 */

export interface HelpGuideSection {
  heading: string;
  /** Paragraphs. Plain text; `**bold**` renders as <strong>. */
  body: string[];
  /** Numbered steps under the paragraphs. */
  steps?: string[];
}

export interface HelpGuide {
  id: string;
  title: string;
  /** One line, shown in the menu. */
  summary: string;
  /** Where it applies, in order. */
  sections: HelpGuideSection[];
}

export const HELP_GUIDES: HelpGuide[] = [
  {
    id: "home",
    title: "Home",
    summary: "This week at a glance, and what needs you.",
    sections: [
      {
        heading: "At a glance",
        body: [
          "**Why you come here.** To see whether this week is fine and whether anything needs you.",
          "**What you see first.** The greeting band, then Roster ahead on the left: each day of the coming weeks, with a red dot on any day that has empty seats. Work spread and Needs you are on the right.",
          "**What you do straight away.** Press Open roster, or follow a row under Needs you.",
        ],
      },
      {
        heading: "What is on the page",
        body: [
          "**Roster ahead** stays in view while the right column scrolls. On a team with many stations the rows sit in groups that open and close. A closed group still shows a red mark for a short day. At 3 months each week is one square. **Solve** stops at the day before the window that holds the first waiting leave request, and **Review** opens the review for those dates. **Needs you** lists what waits for you, one row each with one action. **Work spread** shows how shifts are spread within each level. **Leave and requests** shows the date up to which leave is reviewed, the requests that wait, one line for each month and the leave that came in after the solve.",
          "The Leave & call-blocks link beside Leave and requests opens the leave section at the top of Home. Tasks & announcements and Member overview are on People. Solves is on Roster. Insights is the link at the foot of Home.",
        ],
      },
      {
        heading: "How to",
        body: [],
        steps: [
          "Open roster on the status line opens this week's roster.",
          "Press a Needs you card to go straight to that work.",
          "On a new team, follow Your first roster: add your people, add stations and on-call, bring in leave and call pins, solve one week, publish it. Start opens the next step.",
        ],
      },
      {
        heading: "Good to know",
        body: [
          "Your first roster goes away once anything is published, or when you press I'll do this myself.",
        ],
      },
    ],
  },
  {
    id: "roster",
    title: "Roster",
    summary: "Who works when; solve, check and publish dates.",
    sections: [
      {
        heading: "At a glance",
        body: [
          "**Why you come here.** To see who is working over the next days and fix what is short.",
          "**What you see first.** This week and next: the dates bar with the dates' state (Published, Solved · not published or Not solved), the summary chips, the toolbar and the grid.",
          "**What you do straight away.** Change the dates, open a short day's options, or press Solve these dates.",
        ],
      },
      {
        heading: "The dates bar",
        body: [
          "Earlier and Later move the dates by their own length. Week, Fortnight, 4 weeks, Month and Custom dates pick the length; a custom span is up to 62 days.",
          "The chips under it (Coverage, Fairness, Hours, On-call, Penalty) each open their panel.",
        ],
      },
      {
        heading: "How to",
        body: [],
        steps: [
          "Solve: press Solve in the toolbar, on any dates you are looking at; it starts on the dates on screen and says how many weeks it will solve and roughly how long, before you start it. For dates nobody has solved, Solve these dates in the dates bar does it in one press.",
          "Cover a short day: open the day's Short · options to see who can be called back.",
          "Someone is off sick or on leave: More ▾ → Record absence. Pick the person, the kind and the dates. It takes effect at once and frees their seats for cover.",
          "Re-plan around absences: More ▾ → Fix and re-solve re-solves the dates on screen with the absent people pinned off.",
          "Publish roster publishes every solved date on screen and tells the team.",
        ],
      },
      {
        heading: "Good to know",
        body: [
          "The first solve of new dates is held as drafts: only leads see it until you press Publish roster. A full re-solve of dates already published also waits as drafts: members keep the published roster until you press Publish roster.",
          "Under the grid, Workload, Validation and Notes are tabs; click one to open it.",
        ],
      },
    ],
  },
  {
    id: "people",
    title: "People",
    summary: "Members, invites, and who can work where.",
    sections: [
      {
        heading: "At a glance",
        body: [
          "**Why you come here.** To see who is on the team, add people and check who can work where.",
          "**What you see first.** Over time when manpower planning is on, else List. The views are Over time, List and Workload.",
          "**What you do straight away.** Press Invite, or open More to add a person by name for now. Find a person filters Over time and List.",
        ],
      },
      {
        heading: "How to",
        body: [],
        steps: [
          "Invite sends the person a link. When they accept, they join the team as a member.",
          "More holds Add manual member, Titles, Generate abbreviations and Print legend.",
          "Add manual member puts a name on the team without an account. The roster can seat them; you can invite them later.",
          "List shows each person's level, FTE, posting today and next change. Click a name to open the person panel: postings, Can work at, tags, and Edit details. Profile opens their details.",
          "Workload shows each person's workload against their level, in name order.",
          "Grades and other attributes the rules read are set up in Setup → Attributes.",
        ],
      },
      {
        heading: "Over time",
        body: [
          "When manpower planning is on, the first view, **Over time**, shows a number for each month. Group by Level, Station or Tag. The range pills in the head set the dates for the whole page.",
          'Each row ends with a plain change, such as "Steady" or "+1 from Nov". On Station, a month under the station\'s need reads "4 of 5" in amber, and the row says how many more it needs.',
          "Open a row to see its people as bars with their postings. Click a bar to change that posting, including How firm: Can move or Stays, and Never moves when your service has it on. A solid mint bar stays. An outline bar can move. A dark bar with a pin never moves. Under a short station, Add posting fills in the station and the dates. Tick people and press Rotate out on… to set one last day for all of them.",
        ],
      },
    ],
  },
  {
    id: "setup",
    title: "Setup",
    summary: "Stations, on-call, rules and imports.",
    sections: [
      {
        heading: "At a glance",
        body: [
          "**Why you come here.** To tell Tarii what the roster must cover and the rules it follows.",
          "**What you see first.** The section list: Stations, Attributes, Rules & fairness, Components and Workbook imports, with Guided setup above it.",
          "**What you do straight away.** On a new team, press Guided setup. Otherwise open the section you want to change.",
        ],
      },
      {
        heading: "The sections",
        body: [
          "**Stations** are the places that need someone each day, with how many people each needs. On-call is set on a station too.",
          "**Attributes** hold grades and the other facts the rules read. **Rules & fairness** holds the policies the solver keeps. **Components** turns parts of Tarii on or off.",
          "**Workbook imports** brings in your spreadsheets in seven numbered steps: upload, wards and numbers, people, postings, leave, calls and, if you want, history.",
        ],
      },
      {
        heading: "How to",
        body: [],
        steps: [
          "Press Guided setup and answer five steps: people, stations and on-call, leave and call pins, solve, publish.",
          "On Review and apply, press Preview changes to see what will be written.",
          "Apply. Nothing is saved until this step.",
        ],
      },
      {
        heading: "Good to know",
        body: [
          "Show advanced opens Retention, Custom axes, Workload weights, On-call allocations, Relaxation rules and Postings. An item whose component is off is hidden; More under Components says so.",
          "Every page opens with one title and one sentence. The ? beside a title holds the longer explanation.",
        ],
      },
    ],
  },
  {
    id: "leave",
    title: "Leave & call-blocks",
    summary: "Requests for time off, MCs and on-call blocks.",
    sections: [
      {
        heading: "At a glance",
        body: [
          "**Why you come here.** To see who is off and look at requests before the roster is solved.",
          "**What you see first.** On Home, press Leave & call-blocks beside Leave and requests. The section opens at the top of Home on the List tab, at this month. It has three tabs: Grid, List and Review. A Leave review link from Needs you opens it on Review.",
          "**What you do straight away.** Open Review to look at waiting requests, or paint leave in the Grid.",
        ],
      },
      {
        heading: "How a request moves",
        body: [
          "Members ask for leave, report an MC or block on-call dates from their phone. A request lands as **indicated** and the roster works around it at once; your review is optional.",
          "It becomes **under review** when you accept it or a clash is found, **planned** when a solve used it and **confirmed** when the roster covering it is published. It can also be **declined** or withdrawn.",
        ],
      },
      {
        heading: "How to",
        body: [],
        steps: [
          "Open the Review tab (or the Leave requests to review card) and pick the dates to review.",
          "For each request: Accept into plan, Decline with a reason, or Ask to move.",
          "Leave the rest alone: anything you do not touch goes into the solve as it stands. Review never holds up a solve or a publish.",
        ],
      },
      {
        heading: "Good to know",
        body: [
          "To record leave or an MC for someone, use Record absence under More ▾ on the roster. It takes effect at once. To undo it, remove it here under Leave & call-blocks.",
          "Recording an MC needs sick cover turned on in Setup.",
        ],
      },
    ],
  },
  {
    id: "solves",
    title: "Solves",
    summary: "Every solve: its dates, state, who and when.",
    sections: [
      {
        heading: "At a glance",
        body: [
          "**Why you come here.** To see which dates are solved and published, and who ran each solve.",
          "**What you see first.** At the foot of the Roster page, the Solves section: how many solves, the latest published dates and when you last solved. Solves history opens the full page.",
          "**What you do straight away.** Press Open on a row to see those dates on the roster.",
        ],
      },
      {
        heading: "What a row says",
        body: [
          "Each row is one solve, newest first: the dates, the state (Published, Solved · not published, Not solved or Solving), who ran it and when. The list is read-only.",
          "**Solved** means a roster exists for the dates. Only leads see it until it is **published**; publishing shows it to the team and tells them.",
        ],
      },
      {
        heading: "How to",
        body: [],
        steps: [
          "Open Solves history to see every solve and, under the list, the date line.",
          "Click a solved stretch of the line to open the roster at those dates.",
          "Drag across dates to solve them: Solve when none are solved, Solve unsolved days when some are, View roster when all are.",
        ],
      },
    ],
  },
  {
    id: "next-steps",
    title: "Next steps",
    summary: "The right panel: what still needs you, in order.",
    sections: [
      {
        heading: "At a glance",
        body: [
          "**Why you come here.** To know what still needs you without reading the grid.",
          "**What you see first.** The Next steps button in the top bar, with a count of the steps waiting. It opens a panel over the right edge of the page; the page stays usable behind it.",
          "**What you do straight away.** Follow the first row. It is the most urgent and reads in bold.",
        ],
      },
      {
        heading: "The rows",
        body: [
          "One row per kind of step, each with its count and a link to where the work is, always in the same order: days short on published days, leave requests to review, swaps that need your confirm, changes waiting to be published, the release step, pins that can't be placed, imported calls waiting, and submissions.",
          "A row shows only while its count is above zero. With nothing waiting the panel says so.",
        ],
      },
      {
        heading: "How to",
        body: [],
        steps: [
          "Press Next steps in the top bar.",
          "Press a row. The panel closes and you land where that work is done.",
          "Press Next steps again, Esc or the ✕ to close the panel.",
        ],
      },
      {
        heading: "Good to know",
        body: [
          "Under the steps, Recent activity keeps the three latest changes, with See full activity.",
          "On Home the panel shows team-wide steps only. The browser remembers whether it was open. It shows from 1024 px wide; there is no panel on a phone.",
        ],
      },
    ],
  },
  {
    id: "telegram",
    title: "Telegram",
    summary: "Roster news and quick answers for your members, in a Telegram chat.",
    sections: [
      {
        heading: "At a glance",
        body: [
          "**Why you come here.** To know what your members get once they connect Telegram, and how you see who has.",
          "**What you see first.** On People, a Telegram column before Status: Connected, Not connected or Failed since a date. It shows once Telegram is on for the team (Setup → Components; it is off until you turn it on).",
          "**What you do straight away.** Turn Telegram on in Setup → Components, then tell your members where to connect: their profile → Telegram → Connect Telegram.",
        ],
      },
      {
        heading: "What Connect does",
        body: [
          "The quickest way starts in Telegram: open the Tarii bot, send /start, tap **Connect Tarii**, sign in, then **Connect**. Or from Tarii: a member opens **Telegram** on their profile, taps **Connect Telegram**, then **Start** in the Tarii chat. From then on their roster news comes to the chat instead of email; they can turn email back on per kind of news under Notification preferences.",
          "You can connect your own chat the same way, or from **Telegram** in your account menu (top right).",
          "**What arrives.** Every Inbox card, in the same words: the roster is out, the roster changed, swap and cover asks, how a swap went, leave decisions, your tasks and announcements, when requests close, and the feedback prompt. Several changes inside half an hour are one message, edited in place.",
          "**The commands.** /today, /tomorrow, /week and /next show their own shifts. /roster Nov shows their days on any dates; /whoson who is on a day (as their own app shows it). /sick reports them off sick for the next shift; /requests and /swaps act on what is open; /closing says when requests close; /tell sends you a short note; /settings picks what comes to the chat. /help opens a list of short guides, most with a video, one tap each.",
          "The chat carries only what the member's own app shows them, and messages cannot be forwarded.",
        ],
      },
      {
        heading: "How to",
        body: [],
        steps: [
          "Open Setup → Components and turn Telegram on.",
          "Tell your members: open the Tarii bot in Telegram, send /start, tap Connect Tarii and sign in.",
          "Watch the Telegram column on People: each member reads Connected once they connect.",
        ],
      },
      {
        heading: "Good to know",
        body: [
          "A member disconnects with **Disconnect** on their Telegram screen or **/stop** in the chat. If they block the bot, People shows Failed since that day; a roster-out, swap or requests-open message that cannot be delivered comes by email instead.",
          "When a member reports off sick, you get the alert in the chat too, if you have connected.",
          "/tell notes land in your Next steps as messages from members; only you and your co-leads read them.",
        ],
      },
    ],
  },
  {
    id: "tour",
    title: "The tour",
    summary: "A short walk past the main controls.",
    sections: [
      {
        heading: "At a glance",
        body: [
          "**Why you come here.** To see where the main controls are, in a minute.",
          "**What you see first.** The page dims and a spotlight sits on one control, with a small card: the step, a title and one sentence.",
          "**What you do straight away.** Press Next to move on, or Skip to stop.",
        ],
      },
      {
        heading: "How to",
        body: [],
        steps: [
          "Press the ? Help button in the top bar and choose Show me around.",
          "Walk the stops with Next and Back: your teams, Roster, Setup, Your first roster (or Home), Next steps and Help.",
          "Press Done on the last stop. Escape closes the tour at any point.",
        ],
      },
      {
        heading: "Good to know",
        body: [
          "It shows by itself once, the first time you open a team's home. A stop whose control is not on screen is skipped.",
        ],
      },
    ],
  },
];

export function findGuide(id: string | null | undefined): HelpGuide | undefined {
  if (!id) return undefined;
  return HELP_GUIDES.find((g) => g.id === id);
}
